import { defineStore } from 'pinia';
import axios from 'axios';
import { useAuthStore } from './auth';

const STRAPI_URL = import.meta.env.VITE_STRAPI_URL ?? 'http://localhost:1337';

const normalizeMedia = (entry) => {
	if (!entry) return null;
	const data = entry?.data || entry;
	const attributes = data?.attributes || {};
	return {
		id: data?.id ?? attributes.id ?? null,
		name: attributes.name ?? data?.name ?? '',
		url: attributes.url ?? data?.url ?? '',
		mime: attributes.mime ?? data?.mime ?? '',
		alternativeText: attributes.alternativeText ?? '',
		formats: attributes.formats ?? null,
	};
};

const normalizeProject = (entry) => {
	const attributes = entry?.attributes || entry || {};
	return {
		id: entry?.id ?? attributes.id ?? null,
		documentId: attributes.documentId ?? entry?.documentId ?? null,
		name: attributes.name ?? '',
		businessSettings: attributes.businessSettings ?? {},
		documentPresets: attributes.documentPresets ?? {},
		logo: normalizeMedia(attributes.logo ?? entry?.logo ?? null),
		raw: entry,
	};
};

export const useProjectsStore = defineStore('projects', {
	state: () => ({
		projects: [],
		activeProjectId: null,
		loading: false,
		error: null,
		initialized: false,
	}),
	getters: {
		activeProject(state) {
			return state.projects.find((project) => project.id === state.activeProjectId) || null;
		},
	},
	actions: {
		authHeaders() {
			const authStore = useAuthStore();
			return authStore.token ? authStore.requestHeaders.headers : {};
		},
		reset() {
			this.projects = [];
			this.activeProjectId = null;
			this.loading = false;
			this.error = null;
			this.initialized = false;
		},
		setActiveProject(id) {
			if (id === this.activeProjectId) return;
			this.activeProjectId = id;
		},
		async ensureInitialized() {
			if (this.initialized || this.loading) {
				return;
			}
			await this.fetchProjects();
		},
		async fetchProjects() {
			try {
				this.loading = true;
				this.error = null;
				const response = await axios.get(`${STRAPI_URL}/api/projects`, {
					headers: this.authHeaders(),
					params: {
						populate: ['logo'],
					},
				});
				const entries = response?.data?.data || [];
				const normalized = entries.map(normalizeProject);
				this.projects = normalized;
				if (!this.activeProjectId && normalized.length) {
					this.activeProjectId = normalized[0].id;
				} else if (this.activeProjectId && !normalized.some((p) => p.id === this.activeProjectId)) {
					this.activeProjectId = normalized[0]?.id ?? null;
				}
			} catch (error) {
				this.error = error?.response?.data?.error?.message || error?.message || 'Failed to load projects';
				throw error;
			} finally {
				this.loading = false;
				this.initialized = true;
			}
		},
		async updateProject({ documentId, data }) {
			if (!documentId) {
				throw new Error('documentId is required to update a project');
			}
			const response = await axios.put(
				`${STRAPI_URL}/api/projects/${documentId}`,
				{ data },
				{
					headers: this.authHeaders(),
					params: {
						populate: ['logo'],
					},
				}
			);
			const updated = normalizeProject(response?.data?.data);
			const index = this.projects.findIndex((project) => project.id === updated.id);
			if (index !== -1) {
				this.projects[index] = updated;
			} else {
				this.projects.push(updated);
			}
			return updated;
		},
	},
});
